import { type ReactNode, createContext, useCallback, useContext } from "react";
import { useChatFileDropReceiver } from "src/components/llm/components/chat-file-drop-surface";
import Dropzone, { type FileRejection } from "react-dropzone";
import { FileDragOverlay } from "src/components/llm/components/file-drag-overlay";
import { useFilesStrict } from "src/components/llm/context/file-context";
import { useToast, Box } from "@hightouch/htui";

/**
 * Set while a `PageFileDropZone` is handling drops for a subtree. Composers
 * below it skip their own dropzone: both write into the same `FileProvider`,
 * and drag events bubble, so two live dropzones would attach every file twice.
 */
const PageDropZoneContext = createContext(false);

export const usePageDropZoneActive = (): boolean =>
  useContext(PageDropZoneContext);

type PageFileDropZoneProps = {
  isDisabled?: boolean;
  /** Attachment permission for a bounded chat surface; defaults to the page drop policy. */
  isChatSurfaceDisabled?: boolean;
  onlyInChatSurface?: boolean;
  children: ReactNode;
};

export const PageFileDropZone = ({
  isDisabled = false,
  isChatSurfaceDisabled = isDisabled,
  onlyInChatSurface = false,
  children,
}: PageFileDropZoneProps) => {
  const { addProcessedFiles } = useFilesStrict();
  const { toast } = useToast();
  const hasChatSurface = useChatFileDropReceiver(
    addProcessedFiles,
    isChatSurfaceDisabled,
  );

  const handleDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (isDisabled) return;

      // Handle file rejections
      if (fileRejections.length > 0) {
        const rejection = fileRejections[0];
        const errorMessage =
          rejection?.errors[0]?.message || "File type not supported";
        toast({
          id: "file-drop-error",
          variant: "error",
          title: "File rejected",
          message: errorMessage,
        });
        return;
      }

      if (acceptedFiles.length === 0) return;

      await addProcessedFiles(acceptedFiles);
    },
    [addProcessedFiles, isDisabled, toast],
  );

  if (onlyInChatSurface && !hasChatSurface) return children;
  if (hasChatSurface)
    return (
      <PageDropZoneContext.Provider value>
        {children}
      </PageDropZoneContext.Provider>
    );

  return (
    <Dropzone
      onDrop={handleDrop}
      noClick={true}
      noKeyboard={true}
      disabled={isDisabled}
    >
      {({ getRootProps, getInputProps, isDragActive }) => (
        <>
          <FileDragOverlay isVisible={isDragActive} />
          <Box {...getRootProps()} display="contents">
            <input {...getInputProps()} />
            {/* Only claim the drops we're actually handling — while disabled
                the composer below should fall back to its own dropzone. */}
            <PageDropZoneContext.Provider value={!isDisabled}>
              {children}
            </PageDropZoneContext.Provider>
          </Box>
        </>
      )}
    </Dropzone>
  );
};
